import React from 'react';
import { capitalizeFirstLetter } from 'formatters';
import { Stack, Typography } from '@mui/material';

import { formatCurrencyText, getChangeAction } from './lib';
import { Details } from './Details';

interface Props {
  value: Record<string, any>;
}

const getTradeInTitle = (value: Record<string, any> | null | undefined) =>
  [value?.stockNumber, value?.vin].filter(Boolean).join(' ') || 'Trade-In';

const getTradeInRows = (
  before: Record<string, any> | null | undefined,
  after: Record<string, any> | null | undefined
) => [
  {
    label: 'Trade Value',
    before: formatCurrencyText(before?.tradeInDetails?.tradeValue),
    after: formatCurrencyText(after?.tradeInDetails?.tradeValue),
    rawBefore: before?.tradeInDetails?.tradeValue,
    rawAfter: after?.tradeInDetails?.tradeValue,
  },
  {
    label: 'Amount Owed',
    before: formatCurrencyText(before?.tradeInDetails?.tradeAmountOwed),
    after: formatCurrencyText(after?.tradeInDetails?.tradeAmountOwed),
    rawBefore: before?.tradeInDetails?.tradeAmountOwed,
    rawAfter: after?.tradeInDetails?.tradeAmountOwed,
  },
];

export const EquipmentTradeInsChanges = ({ value }: Props) => {
  const { before, after } = value;

  const toArray = (items: Record<string, any> | Record<string, any>[] | null | undefined): Record<string, any>[] =>
    Array.isArray(items) ? items : items ? [items] : [];

  const beforeItems = toArray(before);
  const afterItems = toArray(after);

  const tradeInIds = Array.from(
    new Set([...beforeItems, ...afterItems].map((item, index) => item?.equipmentId ?? index))
  );

  const findById = (items: Record<string, any>[], id: unknown, index: number) =>
    items.find(item => (item?.equipmentId ?? null) === id) ?? items[index] ?? null;

  return (
    <Stack spacing={1}>
      {tradeInIds.map((id, index) => {
        const beforeItem = findById(beforeItems, id, index);
        const afterItem = findById(afterItems, id, index);
        const action = getChangeAction(beforeItem ?? null, afterItem ?? null);
        const title = getTradeInTitle(afterItem ?? beforeItem);

        return (
          <Stack key={String(id)} spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              {`${capitalizeFirstLetter(action)} trade-in: ${title}`}
            </Typography>

            <Details action={action} rows={getTradeInRows(beforeItem, afterItem)} />
          </Stack>
        );
      })}
    </Stack>
  );
};
